import { ref, type Ref } from 'vue';
import type { FeedbackConfig, FeedbackMetadata } from './types';

interface UseFeedbackReturn {
  isOpen: Ref<boolean>;
  isSubmitting: Ref<boolean>;
  canSubmit: Ref<boolean>;
  submitted: Ref<boolean>;
  submitError: Ref<string | null>;
  open: () => void;
  close: () => void;
  submit: (summary: string, description: string, files: File[]) => Promise<boolean>;
}

export function useFeedback(config: FeedbackConfig): UseFeedbackReturn {
  const isOpen = ref(false);
  const isSubmitting = ref(false);
  const canSubmit = ref(true);
  const submitted = ref(false);
  const submitError = ref<string | null>(null);

  function open() {
    isOpen.value = true;
  }

  function close() {
    isOpen.value = false;
  }

  async function submit(
    summary: string,
    description: string,
    files: File[],
  ): Promise<boolean> {
    isSubmitting.value = true;

    try {
      const identity = config.getUserIdentity();
      const auctionCtx = config.getAuctionContext();

      const metadata: FeedbackMetadata = {
        spaName: config.spaName,
        url: window.location.href,
        route: window.location.pathname,
        userType: identity.type,
        userId: identity.id,
        userEmail: identity.email,
        auctionId: auctionCtx?.id,
        auctionName: auctionCtx?.name,
        browser: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        timestamp: new Date().toISOString(),
      };

      const formData = new FormData();
      formData.append('summary', summary);
      formData.append('description', description);
      formData.append('metadata', JSON.stringify(metadata));
      for (const file of files) {
        formData.append('attachments', file);
      }

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 30_000);
      // No credentials: 'include' — auth uses JWT in the Authorization header
      // (stored in localStorage), not cookies. Adding credentials would require
      // the server to respond with a specific Allow-Origin (not wildcard) and
      // Access-Control-Allow-Credentials: true, making CORS configuration more
      // restrictive without any benefit for this auth pattern.
      let response: Response;
      try {
        response = await fetch(
          `${config.apiBaseUrl}/internal/feedback`,
          {
            method: 'POST',
            body: formData,
            signal: controller.signal,
          },
        );
      } finally {
        window.clearTimeout(timeoutId);
      }

      if (!response.ok) {
        submitError.value = 'Something went wrong. Please try again.';
        return false;
      }

      submitError.value = null;
      canSubmit.value = false;
      submitted.value = true;
      close();

      setTimeout(() => {
        submitted.value = false;
      }, 2000);

      setTimeout(() => {
        canSubmit.value = true;
      }, 30_000);

      return true;
    } catch {
      submitError.value = 'Something went wrong. Please try again.';
      return false;
    } finally {
      isSubmitting.value = false;
    }
  }

  return { isOpen, isSubmitting, canSubmit, submitted, submitError, open, close, submit };
}
